import { Toast, customizeToast } from "./Toast";

const STORAGE_KEY  = "schilliger:wishlist";
const BTN_SELECTOR = "[data-wishlist-product-id]";

const Wishlist = {
	_nonce:         "",
	_ajaxUrl:       "",
	_isLoggedIn:    false,
	_ids:           [],
	_isWishlistPage: false,

	init() {
		this._nonce         = window.themeVars?.wishlistNonce ?? "";
		this._ajaxUrl       = window.themeVars?.ajaxUrl       ?? "";
		this._isLoggedIn    = !!window.themeVars?.user_logged_in;
		this._isWishlistPage = !!document.getElementById("wishlist-page");

		if (this._isLoggedIn) {
			this._ids = (window.themeVars?.wishlist_ids ?? []).map(Number);
			// Fusionne la liste localStorage avec le compte après connexion
			this._syncOnLogin();
		} else {
			this._ids = this._readLocal();
		}

		this._syncButtons();
		this._updateHeaderCount();
		this._bindEvents();

		if (this._isWishlistPage && !this._isLoggedIn) {
			this._renderGuestPage();
		}
	},

	// ── localStorage ────────────────────────────────────────────────────────

	_readLocal() {
		try {
			const raw = window.localStorage.getItem(STORAGE_KEY);
			if (!raw) return [];
			const ids = JSON.parse(raw);
			if (!Array.isArray(ids)) return [];
			return ids.map(Number).filter((n) => Number.isFinite(n) && n > 0);
		} catch {
			return [];
		}
	},

	_writeLocal(ids) {
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
			window.dispatchEvent(
				new StorageEvent("storage", { key: STORAGE_KEY, newValue: JSON.stringify(ids) })
			);
		} catch {
			// noop
		}
	},

	// ── AJAX ─────────────────────────────────────────────────────────────────

	async _request(funcName, data = {}) {
		const params = new URLSearchParams({
			action:   "schilliger_wishlist",
			function: funcName,
		});

		const payload = { nonce: this._nonce, ...data };
		Object.entries(payload).forEach(([key, value]) => {
			params.append(`data[${key}]`, value);
		});

		const res = await fetch(this._ajaxUrl, {
			method:  "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
			body:    params,
		});
		return res.json();
	},

	async _syncOnLogin() {
		const localIds = this._readLocal();
		if (!localIds.length) return;

		const res = await this._request("wishlist_sync", {
			ids: JSON.stringify(localIds),
		});

		if (res.success) {
			this._ids = res.data.wishlist.ids ?? [];
			this._writeLocal([]);
			this._syncButtons();
			this._updateHeaderCount();
		}
	},

	// ── Boutons ───────────────────────────────────────────────────────────────

	_isInWishlist(productId) {
		return this._ids.includes(productId);
	},

	_setButtonState(button, active) {
		button.classList.toggle("is-active", active);
		button.setAttribute("aria-pressed", active ? "true" : "false");
		button.setAttribute("aria-label",   active ? "Retirer de la liste" : "Ajouter à la liste");
		button.setAttribute("title",         active ? "Retirer de la liste" : "Ajouter à la liste");
	},

	_syncButtons(scope = document) {
		scope.querySelectorAll(BTN_SELECTOR).forEach((btn) => {
			const id = Number(btn.dataset.wishlistProductId);
			if (!id) return;
			this._setButtonState(btn, this._isInWishlist(id));
		});
	},

	_updateHeaderCount() {
		const badge = document.getElementById("wishlist-header-count");
		if (!badge) return;
		const count = this._ids.length;
		badge.textContent = count;
		badge.hidden      = count === 0;
	},

	// ── Toggle ────────────────────────────────────────────────────────────────

	async toggle(productId, button) {
		const wasActive = this._isInWishlist(productId);

		if (this._isLoggedIn) {
			const action = wasActive ? "wishlist_remove" : "wishlist_add";
			const nextActive = !wasActive;

			// Mise à jour visuelle immédiate (couleur / is-active) avant la réponse AJAX.
			if (nextActive) {
				this._ids = [...new Set([...this._ids, productId])];
			} else {
				this._ids = this._ids.filter((id) => id !== productId);
			}
			this._setButtonState(button, nextActive);
			this._syncButtons(document);
			this._updateHeaderCount();

			const res = await this._request(action, { product_id: productId });

			if (res.success) {
				this._ids = res.data.wishlist.ids ?? [];
				this._syncButtons(document);
				this._updateHeaderCount();
				Toast({ message: res.data.message, state: "success" });

				if (this._isWishlistPage && wasActive) {
					this._removeItemFromPage(productId);
				}
			} else if (res.data?.code === "guest") {
				if (nextActive) {
					this._ids = this._ids.filter((id) => id !== productId);
				} else {
					this._ids = [...new Set([...this._ids, productId])];
				}
				this._setButtonState(button, wasActive);
				this._syncButtons(document);
				this._updateHeaderCount();
				Toast({ message: res.data.message, state: "info" });
			} else {
				if (nextActive) {
					this._ids = this._ids.filter((id) => id !== productId);
				} else {
					this._ids = [...new Set([...this._ids, productId])];
				}
				this._setButtonState(button, wasActive);
				this._syncButtons(document);
				this._updateHeaderCount();
				Toast({ message: res.data?.message ?? "Erreur.", state: "danger" });
			}
		} else {
			if (wasActive) {
				this._ids = this._ids.filter((id) => id !== productId);
			} else {
				this._ids = [...this._ids, productId];
			}

			this._writeLocal(this._ids);
			this._syncButtons();
			this._updateHeaderCount();
			customizeToast(!wasActive, "Produit ajouté à la liste", "Produit retiré de la liste");

			if (this._isWishlistPage && wasActive) {
				this._removeItemFromPage(productId);
			}
		}
	},

	// ── Page wishlist (visiteurs non connectés) ────────────────────────────────

	async _renderGuestPage() {
		const container = document.getElementById("wishlist-items");
		const emptyMsg  = document.getElementById("wishlist-empty");
		const countEl   = document.getElementById("wishlist-count");
		if (!container) return;

		const ids   = this._readLocal();
		const count = ids.length;
		if (countEl) countEl.textContent = count > 0 ? `(${count})` : "";

		if (!ids.length) {
			if (emptyMsg) emptyMsg.hidden = false;
			return;
		}

		if (emptyMsg) emptyMsg.hidden = true;

		const res = await this._request("wishlist_get_products", {
			ids: JSON.stringify(ids),
		});

		if (res.success) {
			container.innerHTML = res.data.products.map((p) => this._productTpl(p)).join("");
			this._syncButtons(container);
		}
	},

	_removeItemFromPage(productId) {
		const item = document.querySelector(
			`.wishlist-item[data-product-id="${productId}"]`
		);
		if (item) item.remove();

		const count = this._ids.length;
		const countEl  = document.getElementById("wishlist-count");
		const emptyMsg = document.getElementById("wishlist-empty");
		if (countEl) countEl.textContent = count > 0 ? `(${count})` : "";
		if (emptyMsg) emptyMsg.hidden = count > 0;
	},

	_productTpl(product) {
		const nameEsc = product.name.replace(/"/g, "&quot;");
		const addToCart = product.in_stock && product.type === "simple"
			? `<button class="btn btn--dark wishlist-item__add-to-cart" type="button" data-cart-add data-product-id="${product.id}" data-quantity="1">
					Ajouter au panier
				</button>`
			: `<a href="${product.permalink}" class="btn btn--dark wishlist-item__view">Voir le produit</a>`;

		return `
			<article class="wishlist-item" data-wishlist-item data-product-id="${product.id}">
				<a href="${product.permalink}" class="wishlist-item__image-link" tabindex="-1">
					<img src="${product.image}" alt="${nameEsc}" class="wishlist-item__image" width="240" height="240" loading="lazy">
				</a>
				<div class="wishlist-item__body">
					<a href="${product.permalink}" class="wishlist-item__name">${product.name}</a>
					<div class="wishlist-item__price">${product.price}</div>
					${!product.in_stock ? `<span class="wishlist-item__badge wishlist-item__badge--out">Rupture de stock</span>` : ""}
				</div>
				<div class="wishlist-item__footer">
					${addToCart}
					<button
						class="wishlist-item__remove btn--icon"
						type="button"
						data-wishlist-product-id="${product.id}"
						aria-pressed="true"
						aria-label="Retirer ${nameEsc} de la liste"
						title="Retirer de la liste"
					>×</button>
				</div>
			</article>`;
	},

	// ── Partage ───────────────────────────────────────────────────────────────

	async share() {
		if (!this._isLoggedIn) {
			Toast({ message: "Connectez-vous pour partager votre liste.", state: "info" });
			return;
		}

		const btn = document.querySelector("[data-wishlist-share]");
		if (btn) { btn.disabled = true; btn.classList.add("is-loading"); }

		const res = await this._request("wishlist_share");

		if (btn) { btn.disabled = false; btn.classList.remove("is-loading"); }

		if (res.success) {
			try {
				await navigator.clipboard.writeText(res.data.share_url);
				Toast({ message: "Lien copié dans le presse-papiers !", state: "success" });
			} catch {
				Toast({
					message:   `Copiez ce lien : <br><a href="${res.data.share_url}">${res.data.share_url}</a>`,
					state:     "info",
					autoClose: false,
				});
			}
		} else {
			Toast({ message: res.data?.message ?? "Impossible de partager.", state: "danger" });
		}
	},

	// ── Événements ────────────────────────────────────────────────────────────

	_bindEvents() {
		document.addEventListener("click", (e) => {
			const btn = e.target.closest(BTN_SELECTOR);
			if (!btn) return;
			e.preventDefault();
			const id = Number(btn.dataset.wishlistProductId);
			if (!id) return;
			this.toggle(id, btn);
		});

		document.addEventListener("click", (e) => {
			if (!e.target.closest("[data-wishlist-share]")) return;
			this.share();
		});

		if (!this._isLoggedIn) {
			window.addEventListener("storage", (e) => {
				if (e.key !== STORAGE_KEY) return;
				this._ids = this._readLocal();
				this._syncButtons();
				this._updateHeaderCount();
			});
		}

		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === "attributes" && mutation.attributeName === "data-wishlist-product-id") {
					const t = mutation.target;
					if (t instanceof HTMLElement && t.matches(BTN_SELECTOR)) {
						this._syncButtons(document);
					}
					return;
				}
				if (mutation.type !== "childList") {
					return;
				}
				mutation.addedNodes.forEach((node) => {
					if (!(node instanceof HTMLElement)) return;
					if (node.matches?.(BTN_SELECTOR)) {
						this._syncButtons(node.parentElement ?? document);
						return;
					}
					if (node.querySelector?.(BTN_SELECTOR)) {
						this._syncButtons(node);
					}
				});
			});
		});

		observer.observe(document.body, {
			childList:        true,
			subtree:          true,
			attributes:       true,
			attributeFilter:  ["data-wishlist-product-id"],
		});
	},
};

export { Wishlist };
